import { useEffect, useState } from "react";
import { Users, Link2, Plus, Trash2, Loader2, Copy, CheckCircle2, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

export default function TeamPage() {
  const { organizer } = useAuth();
  const { t } = useLang();
  const [invitations, setInvitations] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const load = () => {
    Promise.all([api.get("/my/invitations"), api.get("/my/members")])
      .then(([inv, mem]) => {
        setInvitations(inv.data);
        setMembers(mem.data);
      })
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const createInvitation = async () => {
    setCreating(true);
    const email = inviteEmail.trim();
    try {
      const { data } = await api.post("/my/invitations", email ? { email } : {});
      setInvitations((prev) => [{ ...data, active: true, used_at: null }, ...prev]);
      setInviteEmail("");
      if (data.email_sent) {
        toast.success(t("Invitasjonen er sendt på e-post til {email}", { email: data.sent_to || email }));
      } else if (data.email_message) {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.warning(`${t(data.email_message)} ${t("Lenken er kopiert.")}`);
        } catch {
          toast.warning(t(data.email_message));
        }
      } else {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.success(t("Invitasjonslenken er opprettet og kopiert – send den til medarbeideren"));
        } catch {
          toast.success(t("Invitasjonslenken er opprettet"));
        }
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const copyInvite = async (inv) => {
    const url = inv.url || `${window.location.origin}/invitasjon/${inv.code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("Lenken er kopiert"));
    } catch {
      toast.error(t("Kunne ikke kopiere lenken"));
    }
  };

  const deleteInvite = async (inv) => {
    try {
      await api.delete(`/my/invitations/${inv.id}`);
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      toast.success(t("Invitasjonen er slettet"));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/my/members/${m.id}`);
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      toast.success(t("{name} har ikke lenger tilgang", { name: m.name }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const pendingStatus = organizer?.status === "pending";

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="team-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">{t("Team og invitasjoner")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("Inviter medarbeidere til {org} – de kan opprette og publisere arrangementer, og trenger ingen rolle i Brønnøysundregistrene.", { org: organizer?.org_name })}
          </p>
        </div>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Link2 className="w-5 h-5" /> {t("Invitasjonslenker")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingStatus && (
              <p className="text-sm text-muted-foreground" data-testid="team-pending-note">
                {t("Kontoen venter på godkjenning – invitasjoner blir tilgjengelig når kontoen er godkjent.")}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder={t("kollega@firma.no (valgfritt)")}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={creating || pendingStatus}
                className="sm:max-w-xs"
                data-testid="invite-email-input"
              />
              <Button onClick={createInvitation} disabled={creating || pendingStatus} size="sm" className="h-10 sm:h-9" data-testid="create-invitation-button">
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : inviteEmail.trim() ? (
                  <><Mail className="w-4 h-4 mr-1.5" />{t("Opprett og send på e-post")}</>
                ) : (
                  <><Plus className="w-4 h-4 mr-1.5" />{t("Ny invitasjon")}</>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("Én lenke gir én medarbeider tilgang. Lenken er gyldig i 7 dager.")}{" "}
              {t("Oppgir du e-post, sendes lenken direkte til kollegaen (krever aktivert e-postutsending) – ellers kopieres den så du kan dele den selv.")}
            </p>
            {loading ? (
              <Skeleton className="h-16 w-full rounded-xl" />
            ) : invitations.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="invitations-empty">{t("Ingen invitasjoner enda.")}</p>
            ) : (
              invitations.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5" data-testid={`invitation-row-${inv.code}`}>
                  <span className="font-mono text-sm font-semibold">{inv.code}</span>
                  {inv.used_at ? (
                    <Badge variant="secondary" className="gap-1"><CheckCircle2 className="w-3 h-3" /> {t("Brukt av {name}", { name: inv.used_by })}</Badge>
                  ) : (
                    <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))] gap-1">
                      <Clock className="w-3 h-3" /> {t("Aktiv")}
                    </Badge>
                  )}
                  {inv.sent_to && !inv.used_at && (
                    <Badge variant="secondary" className="gap-1 hidden md:inline-flex" data-testid={`invitation-sent-to-${inv.code}`}>
                      <Mail className="w-3 h-3" /> {inv.email_sent ? t("Sendt til") : t("Tiltenkt")} {inv.sent_to}
                    </Badge>
                  )}
                  <span className="flex-1 text-xs text-muted-foreground text-right hidden sm:block">
                    {t("Utløper {date}", { date: formatDateTime(inv.expires_at) })}
                  </span>
                  {!inv.used_at && (
                    <button type="button" onClick={() => copyInvite(inv)} className="rounded-full hover:bg-secondary p-1.5" data-testid={`invitation-copy-${inv.code}`} aria-label={t("Kopier lenke")}>
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  <button type="button" onClick={() => deleteInvite(inv)} className="rounded-full hover:bg-secondary p-1.5 text-muted-foreground hover:text-destructive" data-testid={`invitation-delete-${inv.code}`} aria-label={t("Slett invitasjon")}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Users className="w-5 h-5" /> {t("Medarbeidere")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <Skeleton className="h-16 w-full rounded-xl" />
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="members-empty">
                {t("Ingen medarbeidere enda. Opprett en invitasjonslenke og send den til en kollega.")}
              </p>
            ) : (
              members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5" data-testid={`member-row-${m.email}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                  </div>
                  <button type="button" onClick={() => removeMember(m)} className="rounded-full hover:bg-secondary p-1.5 text-muted-foreground hover:text-destructive" data-testid={`member-remove-${m.email}`} aria-label={t("Fjern medarbeider")}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
